import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import {
    Features,
    getFeatureFlagValue,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import type { IDirectoryEntry } from '@experiences/interfaces';
import {
    UiProgressButton,
    UiText,
} from '@experiences/ui-common';
import {
    getSortedUserBundleConfigurations,
    useModalState,
    useNavigateWithParams,
    useShowDialog,
} from '@experiences/util';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import type { SourceFilter } from '@uipath/portal-shell/loader';
import { PortalPeoplePicker } from '@uipath/portal-shell-react';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Controller,
    FormProvider,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useSWR, { useSWRConfig } from 'swr';

import useUserInfo from '../../../auth/hooks/UserInfo';
import { notificationType } from '../../../common/constants/Constant';
import * as RouteNames from '../../../common/constants/RouteNames';
import { useOrchRoleAssignmentForAX } from '../../../common/hooks/useOrchRoleAssignmentForAX';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import type {
    IGroupPickerAllocations,
    QuotaDefinitionRequest,
} from '../../../common/interfaces/licenses';
import { groupUrl } from '../../../services/identity/GroupService';
import type { ISubmitGroupAllocations } from '../../../services/licensing/accountant/UserLicenseService';
import {
    deleteGroupLicenseAllocation,
    getAccountLicenseAllocation,
    getGroupLicenseAllocation,
    putGroupLicenseAllocation,
    userLicenseUrl,
} from '../../../services/licensing/accountant/UserLicenseService';
import {
    getQuotaDefinitionsByScope,
    setQuotaCollectionByScope,
} from '../../../services/licensing/management/QuotaManagementService';
import {
    getUserLicenseAllocation,
    userLicenseUri,
} from '../../../services/licensing/UserLicenseService';
import {
    accountGlobalId,
    EnableUserLicensingSelector,
} from '../../../store/selectors';
import { decodeSanitizedHtml } from '../../../util/DecodeSanitizedHtml';
import { UiDrawer } from '../../common/UiDrawer';
import UiForm from '../../common/UiForm';
import { useMigrationStateRevalidator } from '../flexToUniMigration/useMigrationStateRevalidator';
import { LicensingAllocationFormComponent } from './LicensingAllocationFormComponent';
import { sendPendoTrackEventForAKit } from './LicensingHelpers';

const useStyles = makeStyles(theme =>
    createStyles({
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        body: { margin: '0px 24px' },
        cancelButton: { marginRight: '10px' },
        groupText: {
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
            lineHeight: '20px',
            style: 'normal',
        },
        groupAllocationMessage: { marginTop: '24px' },
        groupPicker: { marginTop: '15px' },
        heading: {
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
            fontWeight: 600,
            marginTop: '15px',
            marginBottom: '2px',
        },
        warningText: {
            fontSize: '14px',
            marginBottom: '16px',
            fontWeight: 600,
        },
    }),
);

const GROUP_QUOTA_COLLECTION: string = 'Licensing_GroupRules';
const GROUP_QUOTA_SCOPE_TYPE = 'GROUP';

const AddEditGroupAllocationComponent: React.FC<{ type: 'add' | 'edit' }> = ({ type }) => {
    const EnableExternalUserLicense = useFeatureFlagValue(Features.EnableExternalUserLicense.name);
    const EnableGroupQuotaManagement = useFeatureFlagValue(Features.EnableGroupQuotaManagement.name);
    const EnableBulkAllocationUsersAndGroups = getFeatureFlagValue(Features.EnableBulkAllocationUsersAndGroups.name);

    const classes = useStyles();
    const createDialog = useShowDialog();
    const { formatMessage: translate } = useIntl();
    const { createNotification } = useUiSnackBar();
    const setErrorMessage = useCentralErrorSetter();
    const accountGlobalGuid = useSelector(accountGlobalId);
    const { token } = useUserInfo();

    const navigate = useNavigateWithParams();
    const { state } = useLocation();
    const group = useMemo(() => state?.group, [ state ]);
    const groups = useMemo(() => state?.groups, [ state ]);
    const isBulkEdit = useMemo(() => state?.isBulkEdit, [ state ]);
    const previousLocation = useMemo(() => state?.previousLocation!, [ state ]);

    const EnableUserLicensing = useSelector(EnableUserLicensingSelector);

    const { getErrorMessage } = useGetErrorInfo();

    const {
        open, close,
    } = useModalState(previousLocation ?? RouteNames.Licensing);

    const {
        showUseOrchRoleAssignmentDialogForAX, isLoadingTenantOrchRoleInfo,
    } = useOrchRoleAssignmentForAX();

    const [ loading, setLoading ] = useState(false);

    const defaultPeoplePickerGroup: IGroupPickerAllocations = useMemo(() => ({
        id: '',
        name: '',
        group: undefined,
        userBundleLicenses: [],
        useExternalLicense: false,
    }), []);

    const methods = useForm<IGroupPickerAllocations>({
        mode: 'onChange',
        defaultValues: defaultPeoplePickerGroup,
    });

    const isEdit = useMemo(() => type === 'edit', [ type ]);
    const isMultipleGroups = isBulkEdit && groups && groups.length > 0;
    const isSingleGroup = !isMultipleGroups && group;
    const showGroupPicker = useMemo(() => !isEdit && group == null && !isMultipleGroups, [ isEdit, group, isMultipleGroups ]);
    const {
        handleSubmit, getValues, setValue, formState, reset, control, clearErrors, setError, watch, formState: { errors },
    } = methods;

    const { isDirty } = formState;

    const title = useMemo(
        () => {
            if (isMultipleGroups) {
                return 'CLIENT_EDIT_BULK_GROUP_ALLOCATIONS';
            }
            return isEdit
                ? 'CLIENT_GROUPS_EDIT_GROUP_ALLOCATION_RULE'
                : showGroupPicker
                    ? 'CLIENT_CREATE_GROUP_ALLOCATION_RULE'
                    : 'CLIENT_GROUPS_CREATE_GROUP_ALLOCATION_RULE';
        },
        [ isEdit, showGroupPicker, isMultipleGroups ],
    );

    const drawerTitle = useMemo(() => {
        if (isMultipleGroups) {
            return translate({ id: 'CLIENT_EDIT_BULK_GROUP_ALLOCATIONS' }, { count: groups.length });
        } else if (isSingleGroup) {
            return translate({ id: title }, { 0: group?.name });
        }
        return translate({ id: title });
    }, [ isMultipleGroups, isSingleGroup, groups?.length, group, translate, title ]);

    const { mutate } = useSWRConfig();
    const revalidateMigrationState = useMigrationStateRevalidator();

    const {
        data: editGroupData, isValidating: editGroupLoading,
    } = useSWR(
        isEdit && group ? {
            url: `${userLicenseUrl}/group`,
            id: group.id,
        } : null,
        getGroupLicenseAllocation,
    );

    const {
        data: groupQuota, isValidating: editGroupQuotaLoading,
    } = useSWR(
        isEdit && group && EnableGroupQuotaManagement ? {
            serviceScopeId: accountGlobalGuid,
            quotaScopeId: group.id,
            collection: GROUP_QUOTA_COLLECTION,
        } : null,
        getQuotaDefinitionsByScope,
    );

    const {
        data: addGroupData, isValidating: addGroupLoading,
    } = useSWR(
        !isEdit ? {
            url: `${userLicenseUri}/api/account/${accountGlobalGuid}/user-license`,
            accountGlobalId: accountGlobalGuid,
        } : null,
        getUserLicenseAllocation,
    );

    const {
        data: organizationLicenses, isValidating: loadingOrgLicenses,
    } = useSWR(
        isMultipleGroups ? userLicenseUrl : null,
        getAccountLicenseAllocation,
    );

    const validUserBundleCodes = useMemo(() => getSortedUserBundleConfigurations().map(s => s.code), []);

    const availableBulkBundles = useMemo(() => {
        if (!isMultipleGroups || !organizationLicenses) {
            return null;
        }

        return organizationLicenses
            .filter((license: any) => license.allocated !== undefined && license.total !== undefined)
            .filter((license: any) => license.allocated !== 0 || license.total !== 0)
            .map((license: any) => ({
                code: license.code,
                allocated: license.allocated,
                total: license.total,
                inUse: license.inUse || 0,
                unlimited: license.unlimited,
                quotaConfig: {
                    enabled: false,
                    value: 0,
                },
            }));
    }, [ isMultipleGroups, organizationLicenses ]);

    const hasWarningCondition = useMemo(() => {
        if (!isEdit) {
            return false;
        }

        if (!isMultipleGroups) {
            return group?.userLicenseBundlesUnavailable && group.userLicenseBundlesUnavailable.length > 0;
        }

        return groups?.some((g: any) => g.userLicenseBundlesUnavailable && g.userLicenseBundlesUnavailable.length > 0) || false;
    }, [ isEdit, isMultipleGroups, group?.userLicenseBundlesUnavailable, groups ]);

    const [ allocatedUserBundles, availableUserBundles, useExternalLicense, hasFilteredLicenses ] = useMemo(
        () => {
            if (isMultipleGroups) {
                return [
                    [],
                    availableBulkBundles || [],
                    false,
                    false,
                ];
            } else if (isEdit) {
                const groupUnavailableLicenses = group?.userLicenseBundlesUnavailable || [];
                const allocatedLicenses = editGroupData?.allocatedUserBundles || (editGroupData as any)?.userBundleLicenses || [];
                const originalAllocatedLicenses = allocatedLicenses.slice();

                const filteredAllocatedBundles = allocatedLicenses
                    .filter((licenseCode: string) => EnableBulkAllocationUsersAndGroups ? !groupUnavailableLicenses.includes(licenseCode) : true);
                const filteredAvailableBundles = editGroupData?.availableUserBundles
                    .filter(p => validUserBundleCodes.includes(p.code))
                    .filter(p => EnableBulkAllocationUsersAndGroups ? !groupUnavailableLicenses.includes(p.code) : true)
                    .map(bundle => {
                        const matchingQuota = groupQuota?.find(q => q.segment === bundle.code);
                        return {
                            ...bundle,
                            quotaConfig: {
                                enabled: matchingQuota ? matchingQuota.limit > 0 : false,
                                value: matchingQuota ? matchingQuota.limit : 0,
                            },
                            inUse: bundle.inUse || 0,
                        };
                    });

                const licensesWereFiltered = EnableBulkAllocationUsersAndGroups &&
                    groupUnavailableLicenses.length > 0 &&
                    originalAllocatedLicenses.some((license: string) => groupUnavailableLicenses.includes(license));

                return [
                    filteredAllocatedBundles,
                    filteredAvailableBundles,
                    editGroupData?.useExternalLicense,
                    licensesWereFiltered,
                ];
            }
            return [
                [],
                addGroupData?.filter(ubl => ubl.total > 0).filter(p => validUserBundleCodes.includes(p.code)),
                false,
                false,
            ];

        },
        [
            isMultipleGroups,
            availableBulkBundles,
            editGroupData,
            addGroupData,
            isEdit,
            validUserBundleCodes,
            groupQuota,
            group?.userLicenseBundlesUnavailable,
            EnableBulkAllocationUsersAndGroups,
        ],
    );

    useEffect(() => {
        if (!EnableUserLicensing || (!group && !isMultipleGroups && isEdit)) {
            console.warn('Feature not enabled or missing data. Closing...');
            navigate('/invalidurl');
        }
    }, [ isEdit, EnableUserLicensing, group, isMultipleGroups, navigate ]);

    useEffect(() => {
        if (isMultipleGroups) {
            reset(
                merge(defaultPeoplePickerGroup, {
                    userBundleLicenses: [],
                    useExternalLicense: false,
                    userBundleQuotas: [],
                }),
            );
        } else if (allocatedUserBundles) {
            const initialQuotas = allocatedUserBundles.map((bundleCode: string) => {
                const matchingQuota = groupQuota?.find(q => q.segment === bundleCode);

                return {
                    userBundleLicense: bundleCode,
                    quotaConfig: {
                        enabled: matchingQuota ? matchingQuota.limit > 0 : false,
                        value: matchingQuota ? matchingQuota.limit : 0,
                    },
                };
            });

            reset(
                merge(defaultPeoplePickerGroup, {
                    userBundleLicenses: allocatedUserBundles,
                    useExternalLicense,
                    userBundleQuotas: initialQuotas,
                }),
            );
        }
    }, [ isMultipleGroups, allocatedUserBundles, useExternalLicense, reset, defaultPeoplePickerGroup, groupQuota ]);

    const confirmAllocationOverrideDialog = useCallback(
        async (groupName: string, licenseSelectionIsEmpty: boolean) => await createDialog({
            title: `${translate({ id: 'CLIENT_CREATE_GROUP_ALLOCATION_RULE' })}`,
            body: (
                <div>
                    <p>
                        {translate({ id: 'CLIENT_OVERRIDE_GROUP_ALLOCATION' }, { 0: groupName })}
                    </p>
                    <p>
                        {translate({ id: 'CLIENT_OVERRIDE_GROUP_ALLOCATION_CONTINUE' })}
                    </p>
                    {licenseSelectionIsEmpty && (
                        <p data-cy="delete-warning">
                            {translate({ id: 'CLIENT_DELETE_EXISTING_GROUP_ALLOCATION_RULE_DETAILS' })}
                        </p>
                    )}
                </div>
            ),
            icon: 'warning',
            primaryButtonText: `${translate({ id: 'CLIENT_SAVE' })}`,
            showCancel: true,
        }),
        [ translate, createDialog ],
    );

    const shouldShowWarningDialog = useCallback(
        (payload: ISubmitGroupAllocations) => {
            const licenseAllocationIsDowngraded = allocatedUserBundles?.some(
                (userBundle: string) => payload.licenses.indexOf(userBundle) === -1,
            );
            const allocationRuleAlreadyExists = allocatedUserBundles?.length !== 0 || useExternalLicense;
            return (
                isEdit &&
                allocationRuleAlreadyExists &&
                (licenseAllocationIsDowngraded || useExternalLicense !== payload.useExternalLicense)
            );
        },
        [ allocatedUserBundles, isEdit, useExternalLicense ],
    );

    const onSubmit = useCallback(
        async (data: IGroupPickerAllocations) => {
            setLoading(true);
            try {
                if (isMultipleGroups) {
                    const licenses = data.useExternalLicense ? [] : data.userBundleLicenses!;
                    const externalLicense = data.useExternalLicense ?? false;
                    const emptyLicenseSelection = licenses.length === 0 && !externalLicense;

                    if (emptyLicenseSelection) {
                        const proceed = await createDialog({
                            title: translate({ id: 'CLIENT_GROUPS_DELETE_GROUP_ALLOCATION_RULE' }),
                            body: (
                                <div>
                                    <p>
                                        {translate(
                                            { id: 'CLIENT_BULK_DELETE_GROUP_ALLOCATION_WARNING' },
                                            { count: groups.length }
                                        )}
                                    </p>
                                    <p data-cy="bulk-delete-warning">
                                        {translate({ id: 'CLIENT_GROUPS_DELETE_GROUP_ALLOCATION_RULE_DETAILS' })}
                                    </p>
                                </div>
                            ),
                            icon: 'warning',
                            showCancel: true,
                            primaryButtonText: translate({ id: 'CLIENT_DELETE' }),
                        });

                        if (!proceed) {
                            setLoading(false);
                            return;
                        }
                    }

                    createNotification(
                        translate(
                            { id: 'CLIENT_PROCESSING_BULK_GROUP_ALLOCATIONS' },
                            { count: groups.length }
                        ),
                        notificationType.DEFAULT
                    );

                    const results = {
                        success: 0,
                        failed: 0,
                        errors: [] as string[],
                    };

                    for (const group of groups) {
                        try {
                            if (emptyLicenseSelection) {
                                await deleteGroupLicenseAllocation(group.id);
                            } else {
                                const payload: ISubmitGroupAllocations = {
                                    groupId: group.id,
                                    licenses,
                                    useExternalLicense: externalLicense,
                                };
                                await putGroupLicenseAllocation(payload);

                                const userBundleQuotas = (data.userBundleQuotas ?? [])
                                    .filter(q => licenses.includes(q.userBundleLicense));

                                const quotaPayloads: QuotaDefinitionRequest[] = userBundleQuotas.filter(q => q.quotaConfig.enabled).map(q => ({
                                    collection: GROUP_QUOTA_COLLECTION,
                                    quotaScopeId: group.id,
                                    quotaScopeType: GROUP_QUOTA_SCOPE_TYPE,
                                    segment: q.userBundleLicense,
                                    limit: q.quotaConfig?.enabled ? (q.quotaConfig?.value || 0) : 0,
                                }));

                                if (EnableGroupQuotaManagement && quotaPayloads.length > 0) {
                                    await setQuotaCollectionByScope({
                                        serviceScopeId: accountGlobalGuid,
                                        quotaScopeId: group.id,
                                        collection: GROUP_QUOTA_COLLECTION,
                                        quota: quotaPayloads,
                                    });
                                }
                            }
                            results.success++;
                        } catch (groupError) {
                            console.error(`Failed to update allocations for group ${group.name}:`, groupError);
                            results.failed++;
                            results.errors.push(`${group.name}: ${groupError}`);
                        }
                    }

                    if (results.success === groups.length) {
                        createNotification(
                            translate(
                                { id: 'CLIENT_SUCCESS_BULK_GROUP_ALLOCATIONS' },
                                { count: groups.length }
                            ),
                            notificationType.SUCCESS
                        );
                    } else if (results.success > 0) {
                        createNotification(
                            translate(
                                { id: 'CLIENT_PARTIAL_SUCCESS_BULK_GROUP_ALLOCATIONS' },
                                {
                                    success: results.success,
                                    total: groups.length,
                                }
                            ),
                            notificationType.ERROR
                        );
                    } else {
                        createNotification(
                            translate({ id: 'CLIENT_ERROR_BULK_GROUP_ALLOCATIONS' }),
                            notificationType.ERROR
                        );
                    }

                    // Invalidate cache for all modified groups in bulk operation
                    for (const group of groups) {
                        await mutate({
                            url: `${userLicenseUrl}/group`,
                            id: group.id,
                        });
                    }
                } else {
                    const licenses = data.useExternalLicense ? [] : data.userBundleLicenses!;
                    const externalLicense = data.useExternalLicense ?? false;
                    const emptyLicenseSelection = licenses.length === 0 && !externalLicense;
                    const peoplePickerGroup = data.group satisfies IDirectoryEntry;

                    const userBundleQuotas = data.userBundleQuotas || [];
                    const hasQuotaErrors = userBundleQuotas.some(quota => {
                        if (!quota.quotaConfig?.enabled || !licenses.includes(quota.userBundleLicense)) {
                            return false;
                        }

                        const bundle = availableUserBundles?.find((b: any) => b.code === quota.userBundleLicense);
                        const minValue = bundle?.inUse || 0;

                        return !quota.quotaConfig.value || quota.quotaConfig.value < minValue;
                    });

                    if (hasQuotaErrors) {
                        setLoading(false);
                        createNotification(
                            translate({ id: 'CLIENT_QUOTA_VALIDATION_ERROR' }),
                            notificationType.ERROR
                        );
                        return;
                    }

                    let allocationRuleAlreadyExists;
                    if (showGroupPicker) {
                        if (!data.group) {
                            setError('group', { type: 'required' });
                            setLoading(false);
                            return;
                        } else if (peoplePickerGroup?.identifier == null) {
                            setError('group', { type: 'invalid' });
                            setLoading(false);
                            return;
                        }
                        const groupLicense = await getGroupLicenseAllocation({ id: peoplePickerGroup.identifier });
                        allocationRuleAlreadyExists =
                        groupLicense.allocatedUserBundles.length !== 0 || groupLicense.useExternalLicense;
                        if (allocationRuleAlreadyExists) {
                            const proceed = await confirmAllocationOverrideDialog(
                                peoplePickerGroup.displayName,
                                emptyLicenseSelection,
                            );
                            if (!proceed) {
                                setLoading(false);
                                return;
                            }
                        }
                    }
                    if (emptyLicenseSelection && !isEdit && !allocationRuleAlreadyExists) {
                        setError('userBundleLicenses', { type: 'invalid' });
                        setLoading(false);
                        return;
                    }
                    const groupId = showGroupPicker ? peoplePickerGroup.identifier : group!.id;
                    const payload: ISubmitGroupAllocations = {
                        groupId,
                        licenses,
                        useExternalLicense: externalLicense,
                    };

                    if (!emptyLicenseSelection) {
                        if (shouldShowWarningDialog(payload)) {
                            const proceed = await createDialog({
                                title: translate({ id: 'CLIENT_SAVE_CHANGES' }),
                                body: `${translate({ id: 'CLIENT_EDIT_GROUP_ALLOCATION_RULE_WARNING' })}`,
                                icon: 'warning',
                                showCancel: true,
                                primaryButtonText: translate({ id: 'CLIENT_SAVE' }),
                            });

                            if (!proceed) {
                                setLoading(false);
                                return;
                            }
                        }
                        await putGroupLicenseAllocation(payload);
                        const userBundleQuotas = (data.userBundleQuotas ?? [])
                            .filter(q => licenses.includes(q.userBundleLicense));

                        const quotaPayloads: QuotaDefinitionRequest[] = userBundleQuotas.filter(q => q.quotaConfig.enabled).map(q => ({
                            collection: GROUP_QUOTA_COLLECTION,
                            quotaScopeId: groupId,
                            quotaScopeType: GROUP_QUOTA_SCOPE_TYPE,
                            segment: q.userBundleLicense,
                            limit: q.quotaConfig?.enabled ? (q.quotaConfig?.value || 0) : 0,
                        }));

                        if (EnableGroupQuotaManagement) {
                            await setQuotaCollectionByScope({
                                serviceScopeId: accountGlobalGuid,
                                quotaScopeId: groupId,
                                collection: GROUP_QUOTA_COLLECTION,
                                quota: quotaPayloads,
                            });
                        }

                        sendPendoTrackEventForAKit(allocatedUserBundles, licenses);
                        if (!allocatedUserBundles?.some((bundle: string) => bundle === 'AKIT') && licenses.includes('AKIT')) {
                            showUseOrchRoleAssignmentDialogForAX();
                        }

                        createNotification(
                            translate(
                                { id: 'CLIENT_GROUP_UPDATED' },
                                { 0: decodeSanitizedHtml(showGroupPicker ? peoplePickerGroup.displayName : group!.name) },
                            ),
                            notificationType.SUCCESS,
                        );
                        // Invalidate the specific group's data that the form uses
                        await mutate({
                            url: `${userLicenseUrl}/group`,
                            id: showGroupPicker ? peoplePickerGroup.identifier : group!.id,
                        });
                        await mutate({
                            url: `${groupUrl}/licenses?partitionGlobalId=${accountGlobalGuid}`,
                            partitionGlobalId: accountGlobalGuid,
                        });
                    } else {
                        if (isEdit) {
                            const proceed = await createDialog({
                                title: translate({ id: 'CLIENT_GROUPS_DELETE_GROUP_ALLOCATION_RULE' }),
                                body: `${translate({ id: 'CLIENT_GROUPS_DELETE_GROUP_ALLOCATION_RULE_DETAILS' })}`,
                                icon: 'warning',
                                showCancel: true,
                                primaryButtonText: translate({ id: 'CLIENT_DELETE' }),
                            });
                            if (!proceed) {
                                setLoading(false);
                                return;
                            }
                        }
                        await deleteGroupLicenseAllocation(payload.groupId);
                        createNotification(
                            translate(
                                { id: 'CLIENT_GROUPS_DELETE_GROUP_ALLOCATION_RULE_SUCCESS' },
                                { 0: showGroupPicker ? peoplePickerGroup.displayName : group!.name },
                            ),
                            notificationType.SUCCESS,
                        );
                        // Invalidate the specific group's data that the form uses
                        await mutate({
                            url: `${userLicenseUrl}/group`,
                            id: showGroupPicker ? peoplePickerGroup.identifier : group!.id,
                        });
                        await mutate(`${userLicenseUrl}/users`);
                        await mutate({
                            url: `${groupUrl}/licenses?partitionGlobalId=${accountGlobalGuid}`,
                            partitionGlobalId: accountGlobalGuid,
                        });
                    }
                }
            } catch (error) {
                setLoading(false);
                setErrorMessage(await getErrorMessage(error));
                close();
                return;
            }
            revalidateMigrationState();
            setLoading(false);
            close(true);
        },
        [
            isMultipleGroups,
            groups,
            close,
            showGroupPicker,
            isEdit,
            group,
            setError,
            confirmAllocationOverrideDialog,
            shouldShowWarningDialog,
            EnableGroupQuotaManagement,
            allocatedUserBundles,
            createNotification,
            translate,
            mutate,
            revalidateMigrationState,
            accountGlobalGuid,
            createDialog,
            showUseOrchRoleAssignmentDialogForAX,
            setErrorMessage,
            getErrorMessage,
        ],
    );

    const orgHasUserLicenses = useMemo(
        () =>
            EnableExternalUserLicense || (availableUserBundles?.length ?? 0) + (allocatedUserBundles?.length ?? 0) > 0,
        [ EnableExternalUserLicense, availableUserBundles?.length, allocatedUserBundles?.length ],
    );

    const peoplePickerChangedCallback = useCallback((event: any) => {
        const prevWithoutChip = omit(getValues('group'), [ 'optionId', 'chipType' ]);
        const curWithoutChip = omit(event.detail.data, [ 'optionId', 'chipType' ]);
        const valuesChanged = !isEqual(prevWithoutChip, curWithoutChip);
        setValue('group', event.detail.data, {
            shouldDirty: valuesChanged,
            shouldValidate: valuesChanged,
        });
    }, [ getValues, setValue ]);

    const peoplePickerErrorCallback = useCallback((event: any) => {
        if (event.detail) {
            setError('group', { type: 'invalid' });
        } else {
            clearErrors('group');
        }
    }, [ clearErrors, setError ]);

    const peoplePickerLoadingCallback = useCallback((event: any) => setLoading(event.detail), []);

    return (
        <UiDrawer
            title={drawerTitle}
            drawerProps={{
                anchor: 'right',
                open,
                onClose: () => close(),
            }}
            themeProps={{ disableGutters: true }}
            loading={editGroupLoading || addGroupLoading || isLoadingTenantOrchRoleInfo || editGroupQuotaLoading || loadingOrgLicenses}
        >
            <FormProvider {...methods}>
                <UiForm
                    onSubmit={handleSubmit(onSubmit)}
                    actions={
                        <>
                            <div className={classes.actions}>
                                <Button
                                    className={classes.cancelButton}
                                    onClick={() => close()}
                                    color="primary">
                                    {translate({ id: 'CLIENT_CANCEL' })}
                                </Button>
                                <UiProgressButton
                                    loading={loading}
                                    disabled={
                                        isMultipleGroups
                                            ? false
                                            : (!isDirty && !hasFilteredLicenses) || // Allow save when licenses were filtered
                                              (type === 'add' && watch('userBundleLicenses')?.length === 0 && !watch('useExternalLicense'))
                                    }
                                    onClick={handleSubmit(onSubmit)}
                                    variant="contained"
                                    data-cy="save-submit-button"
                                >
                                    {translate({ id: 'CLIENT_SAVE' })}
                                </UiProgressButton>
                            </div>
                        </>
                    }
                    isDrawer
                    addScrollPadding
                >
                    {orgHasUserLicenses && (
                        <>
                            {showGroupPicker && (
                                <div
                                    className={classes.groupPicker}
                                    data-cy="group-picker">
                                    <Controller
                                        control={control}
                                        name="group"
                                        render={({ field }) => (
                                            <>
                                                <PortalPeoplePicker
                                                    token={token || undefined}
                                                    sourceFilters={[
                                                        'localGroups',
                                                        'directoryGroups',
                                                    ] satisfies SourceFilter[]}
                                                    value={field.value}
                                                    singleMode
                                                    onPeoplePickerChanged={peoplePickerChangedCallback}
                                                    onPeoplePickerError={peoplePickerErrorCallback}
                                                    onPeoplePickerLoading={peoplePickerLoadingCallback}
                                                    onPeoplePickerResolving={peoplePickerLoadingCallback}
                                                />
                                                {errors.group?.type === 'required' &&
                                                    <FormHelperText
                                                        error
                                                        id="my-helper-text">
                                                        {translate(
                                                            { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                                            { 0: translate({ id: 'CLIENT_GROUPS' }) },
                                                        )}
                                                    </FormHelperText>}
                                            </>
                                        )}
                                    />
                                </div>
                            )}
                            <div className={showGroupPicker ? '' : classes.groupAllocationMessage}>

                                {hasWarningCondition && (
                                    <UiText
                                        className={classes.warningText}
                                        data-cy="group-warning-message">
                                        ⚠️
                                        {' '}
                                        {isMultipleGroups
                                            ? translate({ id: 'CLIENT_GROUP_ALLOCATION_WARNING_MULTIPLE' })
                                            : translate({ id: 'CLIENT_GROUP_ALLOCATION_WARNING_SINGLE' })}
                                    </UiText>
                                )}
                                <UiText className={classes.heading}>
                                    {translate({ id: 'CLIENT_SELECT_LICENSES' })}
                                </UiText>
                                <UiText className={classes.groupText}>
                                    {translate({ id: 'CLIENT_GROUPS_ALLOCATION_RULE_MESSAGE' })}
                                </UiText>
                            </div>
                        </>
                    )}
                    <LicensingAllocationFormComponent
                        availableUserBundles={availableUserBundles}
                        allocatedUserBundles={allocatedUserBundles}
                        userId={isMultipleGroups ? null : undefined}
                        isAllocationForAGroup
                    />
                </UiForm>
            </FormProvider>
        </UiDrawer>
    );
};

export default AddEditGroupAllocationComponent;
